import React, { useState, useMemo, useEffect } from 'react';
import { useApp } from '../context/AppContext';
import { format, parseISO, differenceInDays } from 'date-fns';
import { fmtPatron } from '../utils/dates';
import { es } from 'date-fns/locale';
import { Plus, X, Search, CheckCircle, Clock, AlertTriangle, User, Paperclip, Send, Trash2, Edit2 } from 'lucide-react';

const PRIORIDADES = {
  low:    { label: 'Baja',  color: 'var(--accent)' },
  medium: { label: 'Media', color: 'var(--amber)' },
  high:   { label: 'Alta',  color: 'var(--red)' },
};

const TIPOS = [
  'Envío de cotización',
  'Contactar cliente',
  'Gestión de siniestro',
  'Renovación pendiente',
  'Envío de documentación',
  'Cobranza',
  'Endoso',
  'Otro',
];

// ── Modal nuevo/editar ticket ─────────────────────────────────
function TicketModal({ ticket, onClose }) {
  const { addTicket, updateTicket, users, clients, currentUser } = useApp();
  const isEdit = !!ticket;
  const today = format(new Date(), 'yyyy-MM-dd');

  const [form, setForm] = useState(ticket || {
    title: '', type: 'Envío de cotización', description: '',
    assignedTo: '', assignedToId: '',
    clientId: '', priority: 'medium',
    dueDate: '', attachments: [],
  });
  const set = (k, v) => setForm(f => ({ ...f, [k]: v }));

  const sortedClients = [...(clients || [])].sort((a,b) => a.name?.localeCompare(b.name) || 0);
  const sortedUsers = [...(users || [])].sort((a,b) => a.name?.localeCompare(b.name) || 0);

  const submit = async (e) => {
    e.preventDefault();
    if (isEdit) await updateTicket(ticket.id, { ...form, status: ticket.status });
    else await addTicket({ ...form, status: 'open', createdBy: currentUser?.name, createdAt: new Date().toISOString() });
    onClose();
  };

  return (
    <div className="modal-overlay">
      <div className="modal" style={{ maxWidth: 620 }}>
        <div className="modal-header">
          <h2 style={{ fontFamily: 'var(--font-display)', fontSize: 17, fontWeight: 700 }}>
            {isEdit ? 'Editar ticket' : 'Nuevo ticket'}
          </h2>
          <button onClick={onClose} className="btn btn-secondary btn-sm" style={{ padding: '6px 8px' }}><X size={14} /></button>
        </div>
        <form onSubmit={submit}>
          <div className="modal-body">
            <div className="form-group">
              <label className="form-label">Título *</label>
              <input value={form.title} onChange={e => set('title', e.target.value)} required
                placeholder="Ej: Enviar cotización Todo Riesgo a García" />
            </div>
            <div className="form-grid">
              <div className="form-group">
                <label className="form-label">Tipo</label>
                <select value={form.type} onChange={e => set('type', e.target.value)}>
                  {TIPOS.map(t => <option key={t} value={t}>{t}</option>)}
                </select>
              </div>
              <div className="form-group">
                <label className="form-label">Prioridad</label>
                <select value={form.priority} onChange={e => set('priority', e.target.value)}>
                  {Object.entries(PRIORIDADES).map(([k,v]) => <option key={k} value={k}>{v.label}</option>)}
                </select>
              </div>
            </div>
            <div className="form-grid">
              <div className="form-group">
                <label className="form-label">Asignar a *</label>
                <select value={form.assignedToId} onChange={e => {
                  const u = sortedUsers.find(u => u.id === e.target.value);
                  set('assignedToId', e.target.value);
                  set('assignedTo', u?.name || '');
                }} required>
                  <option value="">Seleccionar agente...</option>
                  {sortedUsers.map(u => <option key={u.id} value={u.id}>{u.name} ({u.role})</option>)}
                </select>
              </div>
              <div className="form-group">
                <label className="form-label">Cliente relacionado</label>
                <select value={form.clientId} onChange={e => set('clientId', e.target.value)}>
                  <option value="">Sin cliente específico</option>
                  {sortedClients.map(c => <option key={c.id} value={c.id}>{c.name}</option>)}
                </select>
              </div>
            </div>
            <div className="form-group">
              <label className="form-label">Fecha límite</label>
              <input type="date" value={form.dueDate || ''} min={today} onChange={e => set('dueDate', e.target.value)} />
            </div>
            <div className="form-group">
              <label className="form-label">Descripción / Detalle</label>
              <textarea value={form.description} onChange={e => set('description', e.target.value)}
                rows={3} placeholder="Detallá qué hay que hacer exactamente..." />
            </div>
          </div>
          <div className="modal-footer">
            <button type="button" onClick={onClose} className="btn btn-secondary">Cancelar</button>
            <button type="submit" className="btn btn-primary">{isEdit ? 'Guardar' : 'Crear ticket'}</button>
          </div>
        </form>
      </div>
    </div>
  );
}

// ── Panel lateral detalle ─────────────────────────────────────
function TicketDetail({ ticketId, onClose }) {
  const { tickets, updateTicket, deleteTicket, clients, currentUser } = useApp();
  const ticket = tickets.find(t => t.id === ticketId);
  const [newNote, setNewNote] = useState('');
  const [newAttach, setNewAttach] = useState('');
  const [editModal, setEditModal] = useState(false);

  if (!ticket) return null;

  const client = clients.find(c => c.id === ticket.clientId) || {};
  const prioridad = PRIORIDADES[ticket.priority] || PRIORIDADES.medium;
  const isOverdue = ticket.dueDate && ticket.dueDate < new Date().toISOString().split('T')[0] && ticket.status !== 'done';

  const complete = async () => {
    await updateTicket(ticket.id, { status: 'done', completedAt: new Date().toISOString(), completedBy: currentUser?.name });
  };

  const reopen = async () => {
    await updateTicket(ticket.id, { status: 'open', completedAt: null });
  };

  const addNote = async () => {
    if (!newNote.trim()) return;
    const note = { id: Date.now().toString(), text: newNote.trim(), author: currentUser?.name, date: new Date().toISOString() };
    await updateTicket(ticket.id, { notes: [...(ticket.notes || []), note] });
    setNewNote('');
  };

  const addAttach = async (nameOverride, base64Override, fileType) => {
    const name = nameOverride || newAttach.trim();
    if (!name) return;
    const att = { id: Date.now().toString(), name, base64: base64Override || null, fileType: fileType || null, addedBy: currentUser?.name, date: new Date().toISOString() };
    await updateTicket(ticket.id, { attachments: [...(ticket.attachments || []), att] });
    setNewAttach('');
  };

  const handleFileUpload = (e) => {
    const file = e.target.files[0];
    if (!file) return;
    if (file.size > 4 * 1024 * 1024) { alert('Máximo 4MB'); return; }
    const reader = new FileReader();
    reader.onload = async (ev) => { await addAttach(file.name, ev.target.result, file.type); };
    reader.readAsDataURL(file);
    e.target.value = '';
  };

  const removeAttach = async (id) => {
    await updateTicket(ticket.id, { attachments: (ticket.attachments || []).filter(a => a.id !== id) });
  };

  const handleDelete = async () => {
    if (window.confirm('¿Eliminar este ticket?')) { await deleteTicket(ticket.id); onClose(); }
  };

  return (
    <div className="side-panel" style={{ position: 'fixed', top: 0, right: 0, bottom: 0, left: 240, zIndex: 1000, display: 'flex', height: '100vh' }}>
      <div style={{ position: 'absolute', inset: 0, background: 'rgba(0,0,0,0.5)', backdropFilter: 'blur(3px)' }} />
      <div style={{
        position: 'relative', marginLeft: 'auto', width: '100%',
        background: 'var(--bg-card)', borderLeft: '1px solid var(--border)',
        display: 'flex', flexDirection: 'column', height: '100vh', maxHeight: '100vh', overflowY: 'auto',
      }}>
        {/* Header */}
        <div style={{ padding: '18px 24px', borderBottom: '1px solid var(--border)', position: 'sticky', top: 0, background: 'var(--bg-card)', zIndex: 10 }}>
          <div style={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', gap: 12 }}>
            <div style={{ flex: 1 }}>
              <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap', marginBottom: 8 }}>
                <span className={`badge ${ticket.status === 'done' ? 'badge-green' : isOverdue ? 'badge-red' : 'badge-blue'}`}>
                  {ticket.status === 'done' ? '✅ Completado' : isOverdue ? '🔴 Vencido' : '🔵 Abierto'}
                </span>
                <span className="badge" style={{ background: prioridad.color + '20', color: prioridad.color }}>{prioridad.label} prioridad</span>
                <span className="badge badge-gray">{ticket.type}</span>
              </div>
              <h2 style={{ fontFamily: 'var(--font-display)', fontSize: 16, fontWeight: 700, lineHeight: 1.3 }}>{ticket.title}</h2>
            </div>
            <div style={{ display: 'flex', gap: 6, flexShrink: 0 }}>
              <button className="btn btn-secondary btn-sm" onClick={() => setEditModal(true)}><Edit2 size={13} /></button>
              <button className="btn btn-danger btn-sm" onClick={handleDelete}><Trash2 size={13} /></button>
              <button className="btn btn-secondary btn-sm" onClick={onClose}><X size={14} /></button>
            </div>
          </div>
        </div>

        <div style={{ padding: 24, display: 'flex', flexDirection: 'column', gap: 20 }}>
          {/* Info */}
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 10 }}>
            {[
              ['👤 Asignado a', ticket.assignedTo || '—'],
              ['📅 Creado', ticket.createdAt ? format(parseISO(ticket.createdAt), "d MMM yyyy", { locale: es }) : '—'],
              ['✍️ Por', ticket.createdBy || '—'],
              ['⏰ Fecha límite', ticket.dueDate ? fmtPatron(ticket.dueDate, 'dd/MM/yyyy') : '—'],
              client.name ? ['🏢 Cliente', client.name] : null,
              ticket.completedAt ? ['✅ Completado', format(parseISO(ticket.completedAt), "d MMM yyyy HH:mm", { locale: es })] : null,
            ].filter(Boolean).map(([k,v]) => (
              <div key={k} style={{ background: 'var(--bg-input)', borderRadius: 8, padding: '10px 14px' }}>
                <div style={{ fontSize: 11, color: 'var(--text-muted)', fontFamily: 'var(--font-display)', fontWeight: 600, marginBottom: 2 }}>{k}</div>
                <div style={{ fontSize: 13, fontWeight: 500 }}>{v}</div>
              </div>
            ))}
          </div>

          {ticket.description && (
            <div className="card">
              <div style={{ fontSize: 11, color: 'var(--text-muted)', fontFamily: 'var(--font-display)', fontWeight: 600, textTransform: 'uppercase', letterSpacing: '0.06em', marginBottom: 8 }}>Descripción</div>
              <p style={{ fontSize: 13, lineHeight: 1.7, whiteSpace: 'pre-wrap', margin: 0 }}>{ticket.description}</p>
            </div>
          )}

          {/* Acción principal */}
          <div style={{ display: 'flex', gap: 10 }}>
            {ticket.status !== 'done' ? (
              <button className="btn btn-primary" onClick={complete} style={{ flex: 1 }}>
                <CheckCircle size={15} /> Marcar como completado
              </button>
            ) : (
              <button className="btn btn-secondary" onClick={reopen} style={{ flex: 1 }}>
                Reabrir ticket
              </button>
            )}
          </div>

          {/* Documentos adjuntos */}
          <div className="card">
            <div style={{ fontSize: 11, color: 'var(--text-muted)', fontFamily: 'var(--font-display)', fontWeight: 600, textTransform: 'uppercase', letterSpacing: '0.06em', marginBottom: 12 }}>
              Documentos y cotizaciones ({(ticket.attachments || []).length})
            </div>
            <div style={{ display: 'flex', flexDirection: 'column', gap: 6, marginBottom: 12 }}>
              {(ticket.attachments || []).length === 0 && <p style={{ fontSize: 13, color: 'var(--text-muted)' }}>Sin documentos adjuntos</p>}
              {(ticket.attachments || []).map(a => (
                <div key={a.id} style={{ display: 'flex', alignItems: 'center', gap: 10, background: 'var(--bg-input)', borderRadius: 8, padding: '8px 12px' }}>
                  <Paperclip size={13} color="var(--accent)" />
                  {a.base64 ? (
  <a href={a.base64} download={a.name} style={{ flex: 1, fontSize: 13, color: 'var(--accent)', textDecoration: 'none', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>📎 {a.name}</a>
) : (
  <span style={{ flex: 1, fontSize: 13 }}>{a.name}</span>
)}  
                  <span style={{ fontSize: 11, color: 'var(--text-muted)' }}>{a.addedBy}</span>
                  <button className="btn btn-secondary btn-sm" style={{ padding: '3px 7px' }} onClick={() => removeAttach(a.id)}><X size={11} /></button>
                </div>
              ))}
            </div>
            <label style={{ display: 'flex', alignItems: 'center', gap: 8, cursor: 'pointer', background: 'var(--accent-dim)', border: '1px dashed var(--accent)', borderRadius: 8, padding: '8px 14px', fontSize: 13, color: 'var(--accent)', marginBottom: 8 }}>
  <Paperclip size={14} /> Subir PDF o documento
  <input type="file" onClick={e => { e.target.value = null; }} accept=".pdf,.doc,.docx,.xls,.xlsx,.png,.jpg" onChange={handleFileUpload} style={{ display: 'none' }} />
</label>
            <div style={{ display: 'flex', gap: 8 }}>
              <input value={newAttach} onChange={e => setNewAttach(e.target.value)}
                onKeyDown={e => e.key === 'Enter' && addAttach()}
                placeholder="Nombre del documento o cotización adjunta..." style={{ flex: 1, fontSize: 13 }} />
              <button className="btn btn-secondary btn-sm" onClick={addAttach} disabled={!newAttach.trim()}><Plus size={13} /></button>
            </div>
          </div>

          {/* Notas */}
          <div className="card">
            <div style={{ fontSize: 11, color: 'var(--text-muted)', fontFamily: 'var(--font-display)', fontWeight: 600, textTransform: 'uppercase', letterSpacing: '0.06em', marginBottom: 12 }}>
              Notas del equipo ({(ticket.notes || []).length})
            </div>
            <div style={{ display: 'flex', flexDirection: 'column', gap: 10, marginBottom: 14 }}>
              {(ticket.notes || []).length === 0 && <p style={{ fontSize: 13, color: 'var(--text-muted)' }}>Sin notas aún</p>}
              {[...(ticket.notes || [])].reverse().map(n => (
                <div key={n.id} style={{ background: 'var(--bg-input)', borderRadius: 8, padding: '10px 14px' }}>
                  <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 4 }}>
                    <span style={{ fontSize: 12, fontWeight: 700, color: 'var(--accent)' }}>👤 {n.author}</span>
                    <span style={{ fontSize: 11, color: 'var(--text-muted)' }}>{format(new Date(n.date), "d MMM · HH:mm", { locale: es })}</span>
                  </div>
                  <p style={{ fontSize: 13, lineHeight: 1.6, margin: 0 }}>{n.text}</p>
                </div>
              ))}
            </div>
            <div style={{ display: 'flex', gap: 8, alignItems: 'flex-end' }}>
              <textarea value={newNote} onChange={e => setNewNote(e.target.value)}
                onKeyDown={e => { if (e.key === 'Enter' && (e.metaKey || e.ctrlKey)) addNote(); }}
                placeholder="Agregar nota... (⌘+Enter para enviar)" rows={2}
                style={{ flex: 1, resize: 'none', fontSize: 13 }} />
              <button className="btn btn-primary" onClick={addNote} disabled={!newNote.trim()} style={{ padding: '10px 14px' }}>
                <Send size={13} />
              </button>
            </div>
          </div>
        </div>
      </div>
      {editModal && <TicketModal ticket={ticket} onClose={() => setEditModal(false)} />}
    </div>
  );
}

// ── Página principal ──────────────────────────────────────────
export default function TicketsPage() {
  const { tickets, users, clients, currentUser } = useApp();
  const [search, setSearch] = useState('');
  const [filterStatus, setFilterStatus] = useState('open');
  const [filterAssigned, setFilterAssigned] = useState('all');
  const [newModal, setNewModal] = useState(false);
  const [selectedId, setSelectedId] = useState(null);
  const todayStr = new Date().toISOString().split('T')[0];

  // Enlace directo desde un email: /tickets?id=XXX abre ese ticket.
  // El modal abre por selectedId aunque el filtro de estado lo oculte de la lista.
  useEffect(() => {
    const id = new URLSearchParams(window.location.search).get('id');
    if (id && (tickets || []).some(t => t.id === id)) {
      setSelectedId(id);
      window.history.replaceState({}, '', window.location.pathname);
    }
  }, [tickets]);

  const myTickets = tickets.filter(t => t.assignedToId === currentUser?.id || t.assignedTo === currentUser?.name);
  const pendingCount = myTickets.filter(t => t.status !== 'done').length;
  const overdueCount = tickets.filter(t => t.dueDate && t.dueDate < todayStr && t.status !== 'done').length;

  const filtered = useMemo(() => {
    return tickets.filter(t => {
      const client = clients.find(c => c.id === t.clientId) || {};
      const q = search.toLowerCase();
      const matchSearch = !search ||
        t.title?.toLowerCase().includes(q) ||
        t.assignedTo?.toLowerCase().includes(q) ||
        t.type?.toLowerCase().includes(q) ||
        client.name?.toLowerCase().includes(q);
      const matchStatus = filterStatus === 'all' || t.status === filterStatus;
      const matchAssigned = filterAssigned === 'all' ||
        (filterAssigned === 'mine' && (t.assignedToId === currentUser?.id || t.assignedTo === currentUser?.name));
      return matchSearch && matchStatus && matchAssigned;
    }).sort((a, b) => {
      // Vencidos primero, luego por fecha límite
      const aOver = a.dueDate && a.dueDate < todayStr && a.status !== 'done';
      const bOver = b.dueDate && b.dueDate < todayStr && b.status !== 'done';
      if (aOver && !bOver) return -1;
      if (!aOver && bOver) return 1;
      return (a.dueDate || '9999') < (b.dueDate || '9999') ? -1 : 1;
    });
  }, [tickets, clients, search, filterStatus, filterAssigned, currentUser]);

  return (
    <div className="page fade-in">
      <div className="page-header">
        <div>
          <h1 className="page-title">Tickets</h1>
          <p className="page-subtitle">
            {pendingCount > 0 && <span style={{ color: 'var(--accent)', fontWeight: 600 }}>{pendingCount} pendientes tuyos · </span>}
            {overdueCount > 0 && <span style={{ color: 'var(--red)', fontWeight: 600 }}>⚠️ {overdueCount} vencidos · </span>}
            {tickets.filter(t => t.status !== 'done').length} abiertos en total
          </p>
        </div>
        <button className="btn btn-primary" onClick={() => setNewModal(true)}><Plus size={15} /> Nuevo ticket</button>
      </div>

      {/* Stats rápidas */}
      <div style={{ display: 'flex', gap: 12, marginBottom: 20, flexWrap: 'wrap' }}>
        {[
          { label: 'Mis pendientes', value: pendingCount, color: 'var(--accent)', onClick: () => { setFilterStatus('open'); setFilterAssigned('mine'); } },
          { label: 'Vencidos', value: overdueCount, color: 'var(--red)', onClick: () => setFilterStatus('open') },
          { label: 'Abiertos total', value: tickets.filter(t => t.status !== 'done').length, color: '#60a5fa', onClick: () => { setFilterStatus('open'); setFilterAssigned('all'); } },
          { label: 'Completados', value: tickets.filter(t => t.status === 'done').length, color: 'var(--text-muted)', onClick: () => setFilterStatus('done') },
        ].map(s => (
          <div key={s.label} onClick={s.onClick} className="card" style={{ flex: 1, minWidth: 110, cursor: 'pointer', padding: '12px 16px', borderLeft: `3px solid ${s.color}` }}>
            <div style={{ fontSize: 11, color: s.color, fontFamily: 'var(--font-display)', fontWeight: 600 }}>{s.label}</div>
            <div style={{ fontSize: 22, fontFamily: 'var(--font-display)', fontWeight: 700, color: s.color }}>{s.value}</div>
          </div>
        ))}
      </div>

      {/* Filtros */}
      <div style={{ display: 'flex', gap: 10, marginBottom: 20, flexWrap: 'wrap' }}>
        <div style={{ position: 'relative', flex: 1, minWidth: 220 }}>
          <Search size={15} style={{ position: 'absolute', left: 12, top: '50%', transform: 'translateY(-50%)', color: 'var(--text-muted)' }} />
          <input value={search} onChange={e => setSearch(e.target.value)} placeholder="Buscar tickets..." style={{ paddingLeft: 36 }} />
        </div>
        <select value={filterStatus} onChange={e => setFilterStatus(e.target.value)} style={{ width: 150 }}>
          <option value="all">Todos</option>
          <option value="open">Abiertos</option>
          <option value="done">Completados</option>
        </select>
        <select value={filterAssigned} onChange={e => setFilterAssigned(e.target.value)} style={{ width: 160 }}>
          <option value="all">Todos los agentes</option>
          <option value="mine">Solo los míos</option>
        </select>
      </div>

      {/* Tabla */}
      <div className="card" style={{ padding: 0, overflow: 'hidden' }}>
        <div className="table-wrap">
          <table>
            <thead>
              <tr>
                <th>Ticket</th>
                <th>Tipo</th>
                <th>Cliente</th>
                <th>Asignado a</th>
                <th>Prioridad</th>
                <th>Fecha límite</th>
                <th>Estado</th>
                <th>Docs</th>
                <th></th>
              </tr>
            </thead>
            <tbody>
              {filtered.map(t => {
                const client = clients.find(c => c.id === t.clientId) || {};
                const prioridad = PRIORIDADES[t.priority] || PRIORIDADES.medium;
                const isOverdue = t.dueDate && t.dueDate < todayStr && t.status !== 'done';
                const daysLeft = t.dueDate ? differenceInDays(parseISO(t.dueDate), new Date()) : null;
                const isMyTicket = t.assignedToId === currentUser?.id || t.assignedTo === currentUser?.name;
                return (
                  <tr key={t.id} style={{ cursor: 'pointer', background: isMyTicket && t.status !== 'done' ? 'rgba(124,108,248,0.03)' : 'transparent' }}
                    onClick={() => setSelectedId(t.id)}>
                    <td style={{ maxWidth: 240 }}>
                      <div style={{ fontWeight: 600, fontSize: 13, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                        {isMyTicket && t.status !== 'done' && <span style={{ color: 'var(--accent)', marginRight: 4 }}>●</span>}
                        {t.title}
                      </div>
                      {t.description && <div style={{ fontSize: 11, color: 'var(--text-muted)', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{t.description}</div>}
                    </td>
                    <td style={{ fontSize: 12, color: 'var(--text-secondary)' }}>{t.type}</td>
                    <td style={{ fontSize: 12 }}>{client.name || '—'}</td>
                    <td style={{ fontSize: 13 }}>
                      <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                        <div style={{ width: 26, height: 26, borderRadius: '50%', background: 'var(--accent-dim)', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 11, fontWeight: 700, color: 'var(--accent)' }}>
                          {(t.assignedTo || '?').charAt(0).toUpperCase()}
                        </div>
                        <span>{t.assignedTo || '—'}</span>
                      </div>
                    </td>
                    <td>
                      <span style={{ display: 'inline-flex', alignItems: 'center', gap: 5, fontSize: 12, fontWeight: 700, color: prioridad.color }}>
                        <span style={{ width: 7, height: 7, borderRadius: '50%', background: prioridad.color }} />
                        {prioridad.label}
                      </span>
                    </td>
                    <td style={{ fontSize: 12 }}>
                      {t.dueDate ? (
                        <span style={{ color: isOverdue ? 'var(--red)' : daysLeft !== null && daysLeft <= 2 ? 'var(--amber)' : 'var(--text-secondary)' }}>
                          {isOverdue && '⚠️ '}
                          {fmtPatron(t.dueDate, 'dd/MM/yyyy')}
                        </span>
                      ) : '—'}
                    </td>
                    <td>
                      {t.status === 'done'
                        ? <span className="badge badge-green">✅ Listo</span>
                        : isOverdue
                          ? <span className="badge badge-red">Vencido</span>
                          : <span className="badge badge-blue">Abierto</span>
                      }
                    </td>
                    <td style={{ fontSize: 12, color: (t.attachments||[]).length > 0 ? 'var(--accent)' : 'var(--text-muted)' }}>
                      {(t.attachments||[]).length > 0 && <Paperclip size={13} />} {(t.attachments||[]).length || '—'}
                    </td>
                    <td>
                      <button className="btn btn-secondary btn-sm" onClick={e => { e.stopPropagation(); setSelectedId(t.id); }}>Abrir</button>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
        {filtered.length === 0 && (
          <div style={{ textAlign: 'center', padding: '48px 20px', color: 'var(--text-muted)' }}>
            <CheckCircle size={32} style={{ marginBottom: 12, opacity: 0.3 }} />
            <p>{filterStatus === 'done' ? 'No hay tickets completados' : 'No hay tickets pendientes'}</p>
            {filterStatus !== 'done' && <button className="btn btn-primary" style={{ marginTop: 12 }} onClick={() => setNewModal(true)}>Crear primer ticket</button>}
          </div>
        )}
      </div>

      {newModal && <TicketModal onClose={() => setNewModal(false)} />}
      {selectedId && <TicketDetail ticketId={selectedId} onClose={() => setSelectedId(null)} />}
    </div>
  );
}
